import React, { useEffect, useMemo } from 'react'
import { Spinner } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import { useText } from '../context/TextContext'
import PageHeader from '../components/PageHeader'
import { AdvancedButton } from '../components/AdvancedButton'
import DealModal from '../components/modals/DealModal'
import { useFirebaseDoc, useFirebaseQuery } from '../hooks/firebaseHooks'
import { Deal } from '../models/shared/Deal'
import { COLLECTIONS } from '../utils/shared/constants'
import { useOrganization } from '../context/OrganizationContext'
import { Offer } from '../models/shared/Offer'
import { getLatestOffer } from '../utils/logic'
import OfferEditor from '../components/offers/OfferEditor'
import { ShareToken } from '../models/shared/ShareToken'
import { addDoc, collection, doc, updateDoc } from 'firebase/firestore'
import { useFirestore } from 'reactfire'
import { createShareTokenLink } from '../utils/shared/links'
import OfferShareModal from '../components/modals/OfferShareModal'
import { Customer } from '../models/shared/Customer'
import { useSaveToast } from '../context/SaveToastContext'
import { Organization } from '../models/shared/Organization'
import { toast } from 'react-toastify'
import OfferLog from '../components/offers/OfferLog'
import { UserRole } from '../models/shared/UserInvitation'

interface Props { }

export default function OffersEditorPage(props: Props) {
    const { dealId } = useParams<{ dealId: string }>()
    const { orgId, orgRole } = useOrganization();
    const text = useText()
    const [showDeal, setShowDeal] = React.useState(false)
    const [currentOfferId, setCurrentOfferId] = React.useState<string>('')
    const [deal] = useFirebaseDoc<Deal>(`${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.DEALS}/${dealId}`);
    const [offers] = useFirebaseQuery<Offer>(`${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.DEALS}/${dealId}/${COLLECTIONS.OFFERS}`)
    const [customer] = useFirebaseDoc<Customer>(`${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.CUSTOMERS}/${deal?.customerId || ''}`);
    const firestore = useFirestore();
    const [showShareModal, setShowShareModal] = React.useState(false);
    const { markSaved, startSaving, stopSaving } = useSaveToast();

    useEffect(() => {
        if (currentOfferId || !offers.length) return;

        const latestOffer = getLatestOffer(offers);
        if (latestOffer) {
            setCurrentOfferId(latestOffer.id || '');
        }

    }, [currentOfferId, offers, setCurrentOfferId])

    const offer = useMemo(() => offers?.find((offer) => offer.id === currentOfferId), [offers, currentOfferId]);

    useEffect(() => {
        if (!offer) return;
        if (offer.shareTokenId) return;

        const createShareTokenAsync = async () => {
            const newShareToken: ShareToken = {
                orgId,
                dealId: dealId || '',
                offerId: offer.id || '',
            };
            const newTokenDoc = await addDoc(collection(firestore, COLLECTIONS.SHARE_TOKENS), newShareToken);

            const offerUpdate: Partial<Offer> = {
                shareTokenId: newTokenDoc.id,
            };
            await updateDoc(
                doc(firestore, `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.DEALS}/${dealId}/${COLLECTIONS.OFFERS}/${offer.id}`),
                offerUpdate);

        }

        createShareTokenAsync();
    }, [offer, dealId, orgId, firestore]);

    if (!deal || !offer) {
        return <Spinner animation='border' className='m-5' />
    }

    const handleSaveTemplate = async () => {
        const id = startSaving();
        const organizationUpdate: Partial<Organization> = {
            defaultOfferBlocks: offer.blocks,
        }

        try {
            await updateDoc(doc(firestore, `${COLLECTIONS.ORGANIZATIONS}/${orgId}`), organizationUpdate);
            markSaved(id);
        } catch (error) {
            console.error('Error saving template:', error);
            toast.error(text.defaultError);
            stopSaving();
        }
    }

    return (
        <>
            <PageHeader
                title={text.offers}
                showBackButton
                ignoreEscape
                actionsPrefix={
                    <AdvancedButton icon='bi-pencil' variant='outline-secondary'
                        onClick={() => setShowDeal(!showDeal)}
                    >{text.deal}</AdvancedButton>
                }
                actions={[
                    {
                        text: text.send,
                        icon: 'bi-send',
                        onClick: () => setShowShareModal(true),
                    },
                    {
                        text: text.preview,
                        icon: 'bi-box-arrow-up-right',
                        link: createShareTokenLink(offer.shareTokenId || '', { offer: true }),
                        newTabLink: true,
                    },
                    {
                        text: text.saveAsTemplate,
                        icon: 'bi-files-alt',
                        onClick: handleSaveTemplate,
                        disabled: orgRole !== UserRole.ADMIN,
                    }
                ]}
            />

            <OfferEditor
                deal={deal}
                offer={offer}
            />

            <OfferLog className='mt-4' offer={offer} />


            {deal && showDeal && <DealModal deal={deal} onClose={() => setShowDeal(false)} />}

            <OfferShareModal customer={customer} offer={showShareModal ? offer : null} onHide={() => setShowShareModal(false)} />
        </>
    )
}
